"use client";
import { useState } from "react";
import Link from "next/link";
import { Menu, X, Github, Linkedin } from "lucide-react";
import Image from "next/image";

const NAV_ITEMS = [
  { href: "/#arbeiten", label: "Arbeiten" },
  { href: "/#leistungen", label: "Leistungen" },
  { href: "/#kontakt", label: "Kontakt" },
] as const;

const SOCIAL_LINKS = [
  { href: "https://github.com/CreateYourReality", Icon: Github, label: "GitHub" },
  { href: "https://www.linkedin.com/in/timo-m%C3%B6wes-466b54286/", Icon: Linkedin, label: "LinkedIn" },
] as const;

const LOGO_SIZE = 48;

const Header = () => {
  const [open, setOpen] = useState(false);

  return (
    <header className="fixed top-4 left-0 z-[var(--z-header)] w-full flex justify-center">
      <div className="w-[calc(100%-2rem)] max-w-7xl flex items-center px-2 md:px-8 py-1 sm:py-3 bg-gradient-to-r from-[#bfe6ff]/80 via-[#8ccaff]/60 to-[#66aafc]/50 backdrop-blur-sm rounded-xl border-4 border-[#1d2b4f] shadow-[6px_8px_0_rgba(29,43,79,0.35)]">
        <nav className="w-full flex justify-between items-center gap-2">
        {/* Logo & Text */}
        <Link href="/" scroll={true}>
          <div className="flex items-center gap-2 cursor-pointer select-none">
            <div className="relative h-10 w-10 lg:h-12 lg:w-12 rounded-full border-4 border-[var(--dark-blue)] bg-[var(--yellow)] pt-1 shadow-[4px_4px_0_rgba(29,43,79,0.25)] overflow-hidden">
              <Image src="/img/möwe.webp" alt="Logo Möwe" width={LOGO_SIZE} height={LOGO_SIZE} className="object-cover rounded-full" />
            </div>
            {/* Text direkt am Logo ab MD */}
            <div className="hidden md:flex flex-col leading-none">
              <span className="text-md font-luckiest text-[#1d2b4f] leading-[0.9]">Webentwicklung</span>
              <span className="text-xl font-luckiest text-[#1d2b4f] leading-[0.9]">Timo Möwes</span>
            </div>
          </div>
        </Link>

        {/* Text Center - nur auf Mobile */}
        <div className="flex md:hidden flex-col leading-none text-center flex-1">
          <span className="text-md font-luckiest text-[#1d2b4f] leading-[0.9]">Webentwicklung</span>
          <span className="text-xl font-luckiest text-[#1d2b4f] leading-[0.9]">Timo Möwes</span>
        </div>

  {/* Desktop Navigation */}
  <ul className="hidden md:flex items-center h-full list-none gap-3">
          {NAV_ITEMS.map(({ href, label }) => (
            <li key={href}>
              <a
                href={href}
                className="text-sm md:text-base font-luckiest px-4 py-2 rounded-sm border-4 border-[var(--dark-blue)] bg-white/90 text-[var(--dark-blue)] shadow-[4px_4px_1px_var(--dark-blue)] hover:bg-[var(--yellow)] transition-colors"
              >
                {label}
              </a>
            </li>
          ))}
        </ul>

        {/* Mobile Hamburger */}
        <button
          className="md:hidden rounded-lg bg-white/80 p-3 min-w-[48px] min-h-[48px] flex items-center justify-center text-[#1d2b4f] shadow-[4px_4px_0_rgba(29,43,79,0.25)] hover:bg-[#ffe066] transition-colors"
          onClick={() => setOpen(!open)}
          aria-label="Toggle menu"
        >
          {open ? <X size={24} /> : <Menu size={24} />}
        </button>
        </nav>
      </div>

      {/* Mobile Dropdown */}
      {open && (
        <div className="absolute top-[5.6rem] left-1/2 -translate-x-1/2 w-[calc(100%-4rem)] max-w-md rounded-lg border-4 border-[var(--dark-blue)] bg-gradient-to-r from-[#bfe6ff]/90 via-[#8ccaff]/70 to-[#66aafc]/60 p-4 flex flex-col items-stretch gap-3 md:hidden">
          {NAV_ITEMS.map(({ href, label }) => (
            <a
              key={href}
              href={href}
              className="text-base font-luckiest px-4 py-3 rounded-full border-4 border-[var(--dark-blue)] bg-white/90 text-[var(--dark-blue)] shadow-[6px_6px_0_var(--dark-blue)] hover:bg-[var(--yellow)] transition-colors"
              onClick={() => setOpen(false)}
            >
              {label}
            </a>
          ))}
          
          <div className="flex gap-3 pt-2">
            {SOCIAL_LINKS.map(({ href, Icon, label }) => (
              <a
                key={href}
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                className="flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-full border-4 border-[var(--dark-blue)] bg-white/90 text-[var(--dark-blue)] shadow-[6px_6px_0_var(--dark-blue)] hover:bg-[var(--light-blue)] hover:text-white transition-colors font-[Poppins] font-semibold text-sm"
                onClick={() => setOpen(false)}
              >
                <Icon size={20} />
                <span className="hidden xs:inline">{label}</span>
              </a>
            ))}
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
